import { type ReactNode } from "react";
import type { ChartData } from "../../components/index.ts";
import { ChartCard } from "../../components/observability/index.ts";
import type { PostureDriftTrendPoint } from "./use-posture.ts";
import { FINDINGS_TREND_AVAILABLE } from "./vocab.ts";

/**
 * Posture trend (task 11.10; design 12.2 "trend"; 12.3 honest states; 12.4 chart with a built-in
 * data-table alternative). The host-drift trend is now presented inside the observability kit's
 * `ChartCard` (observability-ui task 4.1), so the Posture screen reads like the Overview and the
 * other section rollouts. This is PRESENTATION-ONLY — the plotted series is the identical contract
 * `trend`; nothing in the data layer changed.
 *
 * THE HONEST TREND SOURCING (the contract gap this screen surfaces). The design asks for a posture
 * findings/drift trend. The generated contract provides EXACTLY ONE posture time-series: the
 * `trend` array on `GET /posture/drift` (host-drift counts over time). `GET /posture/findings`
 * carries NO series — only the point-in-time `by_code_and_tier` counts and `items`. So:
 *   - the HOST-DRIFT trend is charted from the contract's `trend` series (real data);
 *   - a FINDINGS-over-time trend is NOT fabricated from the snapshot counts. Per the metrics catalog
 *     a live `oathera.posture.findings` gauge exists, but it is a stream metric not exposed by these
 *     REST reads here, so rather than invent a series we show an honest note that a findings trend
 *     is unavailable from the contract (design 12.3 "estimates say so; never fabricate"). The
 *     {@link FINDINGS_TREND_AVAILABLE} flag records this gap in one place.
 *
 * The `ChartCard` wraps the shared `Chart` wrapper, which ALWAYS renders a semantic data-table
 * alternative (design 12.4) and an honest empty state when the window has no points; the card adds
 * the titled panel chrome + live/paused indicator and carries the explanatory source note and the
 * honest findings-trend-unavailable note in its footer. ChartCard's x/xIsMillis default to time/ms,
 * exactly what the drift trend's timestamps use.
 */

export interface PostureTrendProps {
  readonly trend: readonly PostureDriftTrendPoint[];
}

export function PostureTrend(props: PostureTrendProps): ReactNode {
  // Build uPlot's aligned data `[xs, ys]` from the contract trend (timestamp ms, count). The points
  // are already in series order per contract; we sort defensively so the x axis is monotonic.
  const sorted = [...props.trend].sort((a, b) => a.t - b.t);
  const xs = sorted.map((p) => p.t);
  const ys = sorted.map((p) => p.count);
  const data: ChartData = [xs, ys];

  const footer = (
    <div className="pst-trend-footer">
      <p className="pst-section__note">
        Host configuration drift over the selected window. This is the only posture time-series the
        control plane exposes here.
      </p>
      {/* The contract carries no findings-over-time series; say so honestly rather than fabricate
          one from the point-in-time by-code-and-tier counts (design 12.3). */}
      {!FINDINGS_TREND_AVAILABLE && (
        <p className="pst-trend-note" role="note">
          A findings-over-time trend is not available: the posture findings endpoint reports only
          point-in-time counts by code and tier, with no time-series. The findings grid above
          reflects the selected window.
        </p>
      )}
    </div>
  );

  return (
    <ChartCard
      title="Host drift trend"
      data={data}
      series={[{ label: "Drift entries", colorVar: "--accent", measure: "exact" }]}
      empty="No host drift was recorded in this window. Widen the time range to see a trend."
      footer={footer}
    />
  );
}
